/**
 * @typedef {import('./product-groups-data.js').GroupProduct} GroupProduct
 */

/**
 * Loads the shared data layer via a dynamic import of its versioned asset URL
 * (Theme.assets.product_groups_data carries Shopify's `?v=` hash). A static
 * `import './product-groups-data.js'` resolves a bare relative path that the
 * CDN can't version, so a stale copy missing newer exports (e.g.
 * colourToBackground) could be served from cache. Deduped per page load.
 * @type {Promise<typeof import('./product-groups-data.js')> | null}
 */
let dataModulePromise = null;
function loadDataModule() {
  if (dataModulePromise) return dataModulePromise;
  /** @type {any} */
  const theme = /** @type {any} */ (window).Theme;
  const url = theme?.assets?.product_groups_data || './product-groups-data.js';
  dataModulePromise = import(url);
  return dataModulePromise;
}

class ProductCardGroupSelector extends HTMLElement {
  /** @type {string | undefined} */
  productGid;
  /** @type {string | undefined} */
  currentHandle;
  /** @type {boolean} */
  hideSoldOut = false;
  /** @type {Element | null} */
  cardRoot = null;
  /** @type {IntersectionObserver | null} */
  observer = null;
  /** @type {Map<string, GroupProduct> | null} */
  productsByColor = null;
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {typeof import('./product-groups-data.js') | null} */
  #data = null;

  connectedCallback() {
    this.productGid = this.dataset.productGid;
    this.currentHandle = this.dataset.productHandle;
    this.hideSoldOut = this.hasAttribute('data-hide-sold-out');

    // Created up front so an initial load that's still in flight can be
    // aborted on disconnect (the swatch swap re-creates it per click).
    this.#abortController = new AbortController();

    this.cardRoot = this.closest('product-card');

    const observeTarget = this.cardRoot || this.parentElement || this;

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              this.observer?.disconnect();
              this.#loadAndRender();
              break;
            }
          }
        },
        { rootMargin: '200px' }
      );
      this.observer.observe(observeTarget);
    } else {
      this.#loadAndRender();
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.#abortController?.abort();
  }

  async #loadAndRender() {
    try {
      this.#data = await loadDataModule();

      const groups = await this.#data.fetchAllGroups();

      // Bail out if the card was removed (navigation, modal close, infinite-
      // scroll discard, theme-editor re-render) while the shared fetch was in
      // flight — otherwise we'd render into a detached element.
      if (this.#abortController?.signal.aborted || !this.isConnected) return;

      let group = groups.find((g) => g.some((p) => p.id === this.productGid));
      if (!group || group.length <= 1) return;

      const currentProduct = group.find((p) => p.handle === this.currentHandle);
      const currentColour = this.#data.parseMetafieldList(currentProduct?.colour?.value);
      const currentSize = this.#data.parseMetafieldList(currentProduct?.size?.value);

      // `hide_sold_out` removes a colour from the swatches only when NO variant
      // matching the current size is in stock for that colour (and the current
      // product isn't that sold-out variant). This preserves size consistency
      // when swapping — tapping Silver from a Medium card lands on Silver
      // Medium even if it's sold out, so the card surfaces the correct
      // Slutsåld badge + disabled CTA instead of silently falling back to a
      // different size.
      if (this.hideSoldOut) {
        group = group.filter((p) => {
          if (p.availableForSale) return true;
          if (p.handle === this.currentHandle) return true;
          const pSize = this.#data.parseMetafieldList(p.size?.value);
          return pSize != null && pSize === currentSize;
        });
        if (group.length <= 1) return;
      }

      const colors = this.#groupByColor(group, currentSize);
      if (colors.length <= 1) return;

      this.productsByColor = new Map(colors.map((c) => [c.colour, c.product]));
      this.#renderColors(colors, currentColour);
      this.classList.add('product-card-group-selector--loaded');
    } catch (error) {
      console.error('[product-card-group-selector]', error);
    }
  }

  /**
   * Pick one product per colour. Priority:
   *   1. The current product wins for its own colour (keeps the current card).
   *   2. Same size as the current product (keeps the shopper on that size
   *      across colour swaps — even if sold out, so the swapped card shows the
   *      correct Slutsåld state).
   *   3. In stock beats out-of-stock (when no same-size match exists).
   *   4. Otherwise first-seen wins.
   *
   * @param {GroupProduct[]} products
   * @param {string | null} currentSize
   * @returns {Array<{colour: string, product: GroupProduct}>}
   */
  #groupByColor(products, currentSize) {
    /** @type {Map<string, GroupProduct>} */
    const map = new Map();
    for (const product of products) {
      const colour = this.#data.parseMetafieldList(product.colour?.value);
      if (!colour) continue;

      const existing = map.get(colour);
      if (!existing) {
        map.set(colour, product);
        continue;
      }

      // Current card always wins for its own colour slot.
      if (product.handle === this.currentHandle) {
        map.set(colour, product);
        continue;
      }
      if (existing.handle === this.currentHandle) continue;

      const size = this.#data.parseMetafieldList(product.size?.value);
      const existingSize = this.#data.parseMetafieldList(existing.size?.value);
      const productMatchesSize = currentSize != null && size === currentSize;
      const existingMatchesSize = currentSize != null && existingSize === currentSize;

      if (productMatchesSize && !existingMatchesSize) {
        map.set(colour, product);
        continue;
      }
      if (existingMatchesSize && !productMatchesSize) continue;

      // Same size-match status (both match or neither): prefer in-stock.
      if (product.availableForSale && !existing.availableForSale) {
        map.set(colour, product);
      }
    }
    return Array.from(map, ([colour, product]) => ({ colour, product }));
  }

  /**
   * @param {Array<{colour: string, product: GroupProduct}>} colors
   * @param {string | null} currentColour
   */
  #renderColors(colors, currentColour) {
    const fragment = document.createDocumentFragment();

    for (const { colour } of colors) {
      const isCurrent = colour === currentColour;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'product-card-group-selector__color';
      if (isCurrent) {
        button.classList.add('product-card-group-selector__color--active');
        button.setAttribute('aria-current', 'true');
      }
      button.setAttribute('aria-label', colour);
      button.dataset.colour = colour;

      const swatch = document.createElement('span');
      swatch.className = 'product-card-group-selector__color-swatch';
      swatch.style.background = this.#data.colourToBackground(colour);
      button.appendChild(swatch);

      button.addEventListener('click', this.#onSelect.bind(this, colour));
      fragment.appendChild(button);
    }

    this.appendChild(fragment);
  }

  /**
   * @param {string} colour
   * @param {Event} event
   */
  #onSelect(colour, event) {
    event.preventDefault();
    const product = this.productsByColor?.get(colour);
    if (!product || product.handle === this.currentHandle) return;

    this.#swapCard(product, colour);
  }

  /**
   * Swap the entire card in-place by fetching the alternate `?view=card`
   * template and replacing all attributes + children of the existing
   * <product-card> element. We deliberately avoid morph() here because the
   * card has many stateful sub-components (slideshow, variant-picker,
   * add-to-cart-component, product-badges, quick-add) whose state would
   * otherwise be left stale. Full replaceChildren() guarantees every badge,
   * CTA, title, price, image, and selector reflects the new product.
   * The <product-card> element itself is preserved so its own event
   * listeners (click, variantUpdate, slideshowSelect) remain intact.
   * @param {GroupProduct} product
   * @param {string} colour
   */
  async #swapCard(product, colour) {
    const root = this.cardRoot;
    // instanceof alone doesn't catch a detached node — a card discarded by
    // infinite-scroll/route change would otherwise be "swapped" invisibly.
    if (!(root instanceof HTMLElement) || !root.isConnected) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    /** @type {any} */
    const theme = /** @type {any} */ (window).Theme;
    // Strip any trailing slash so it joins cleanly with `/products/...`
    // (localized root_url is e.g. "/en" with no trailing slash).
    const routesRoot = (theme?.routes?.root_url || '/').replace(/\/+$/, '');
    const url = `${routesRoot}/products/${product.handle}?view=card`;

    try {
      const response = await fetch(url, {
        signal: this.#abortController.signal,
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newCard = doc.querySelector('product-card');
      if (!(newCard instanceof HTMLElement)) throw new Error('No product-card in response');
      // Guard against an empty/malformed card (e.g. an app or middleware
      // stripped the body): a real card always has a <product-price>. Without
      // this, replaceChildren would silently swap in a blank card.
      if (!newCard.querySelector('product-price')) {
        throw new Error('Malformed product-card response (no price)');
      }

      // The card may have been removed during the fetch; don't mutate a
      // detached node.
      if (!root.isConnected) return;

      // Mark swapping *after* response is in hand so we don't flash the marker
      // on aborted requests. Cleared unconditionally in `finally`.
      root.setAttribute('data-card-swapping', '');

      // 1) Reconcile attributes on the existing <product-card> root. We
      //    preserve `data-card-swapping` because it's our own transient marker
      //    and isn't part of the fetched response.
      const existingAttrNames = Array.from(root.attributes).map((a) => a.name);
      for (const name of existingAttrNames) {
        if (name === 'data-card-swapping') continue;
        if (!newCard.hasAttribute(name)) root.removeAttribute(name);
      }
      for (const attr of Array.from(newCard.attributes)) {
        if (root.getAttribute(attr.name) !== attr.value) {
          root.setAttribute(attr.name, attr.value);
        }
      }

      // 2) Replace every child with the freshly rendered ones. Forces all
      //    sub-web-components to disconnect → connect so their internal state
      //    (refs, event listeners, slideshow index, quick-add cache) is
      //    rebuilt from scratch against the new product.
      root.replaceChildren(...Array.from(newCard.childNodes));

      // The <product-card> element itself is preserved (not re-inserted), so
      // the browser does NOT re-run its connectedCallback: its `refs` stay
      // cached against the old children and its size-selector/ATC listeners
      // stay bound to now-removed nodes. A swapped card's size click would then
      // hit a stale quick-add ref and never add to cart. Re-bind refs +
      // handlers against the fresh children. (Child custom elements reconnect
      // on their own via replaceChildren; only the preserved parent needs this.)
      if (typeof (/** @type {any} */ (root).reinitAfterSwap) === 'function') {
        /** @type {any} */ (root).reinitAfterSwap();
      }

      this.currentHandle = product.handle;

      root.dispatchEvent(
        new CustomEvent('product-card:change', {
          bubbles: true,
          detail: { handle: product.handle, colour },
        })
      );
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('[product-card-group-selector] swap failed', error);
      }
    } finally {
      root.removeAttribute('data-card-swapping');
    }
  }
}

if (!customElements.get('product-card-group-selector')) {
  customElements.define('product-card-group-selector', ProductCardGroupSelector);
}
